import React from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable, DefinitionList, DataPair } from '../patterns';
import type { DataTableColumn } from '../patterns';
import type { Orchard, SprayMachine, SprayProduct, SprayRound } from '../../services/sprayService';
import {
  calculateNumberOfTanks,
  calculatePerTankDosage,
  calculateTotalPlannedWater,
  calculateTotalProductAmount,
} from '../../utils/planningSummaryCalculations';
import {
  formatLitres,
  formatProductAmount,
  formatTankCount,
} from '../../utils/planningSummaryFormat';

/**
 * Planning Summary on the Edit Page.
 *
 * Track: spray_session_ui_changes_20260502 / Phase 2 / Task 4 (originally
 * Task 2). Phase C2 / Task 11 migrated the chrome from `<Card>`+`<Table>`+
 * `<dl>` to pat-* primitives (`DataTable`, `DefinitionList`). The component
 * is rendered inside the parent `FormSection id="session-summary"` in
 * `SpraySessionForm`, so it does not introduce its own outer section.
 *
 * Pure presentational. Receives the four data inputs as props and renders
 * the four sections (Total Planned Water, Total Product Amounts, Number of
 * Tanks, Per Tank Dosages) per spec §2. Performs no data fetching — the
 * caller (SpraySessionForm) supplies the catalog of products and the
 * resolved orchard / round / machine objects.
 *
 * Tank-related sections (Number of Tanks, Per Tank Dosages) are hidden
 * entirely when the user has not yet selected a machine.
 */

interface Props {
  orchard: Orchard | null;
  round: SprayRound | null;
  machine: SprayMachine | null;
  products: SprayProduct[];
}

interface ProductAmountRow {
  _id: string;
  productName: string;
  amountText: string;
}

const DASH = '—';

const PlanningSummary: React.FC<Props> = ({ orchard, round, machine, products }) => {
  const { t } = useTranslation();

  // Legacy round detection: pre-schema rounds have no waterPerHectare on
  // disk. Distinct from "missing area" — the latter still renders the
  // calculated rows with em-dashes; legacy collapses the whole body.
  const isLegacyRound = round != null && round.waterPerHectare == null;

  if (isLegacyRound) {
    return (
      <p className="pat-form-section__description" data-testid="planning-summary">
        {t('spray.planningSummary.legacyRoundFallback')}
      </p>
    );
  }

  // Resolve the productList IDs against the catalogue once. Orphaned IDs
  // (deleted products that still appear on a round) are dropped silently.
  const productsForRound: SprayProduct[] = (round?.productList ?? [])
    .map(id => products.find(p => p._id === id))
    .filter((p): p is SprayProduct => p != null);

  const totalWater = calculateTotalPlannedWater(orchard, round);
  const machineHasNoTankVolume = machine != null && !machine.tankVolume;

  // Total Product Amounts rows.
  const totalAmountRows: ProductAmountRow[] = productsForRound.map(p => {
    const amt = calculateTotalProductAmount(totalWater?.value ?? null, p);
    return {
      _id: p._id,
      productName: p.productName,
      amountText: amt ? formatProductAmount(amt) : DASH,
    };
  });

  // Per Tank Dosages rows.
  const perTankRows: ProductAmountRow[] = productsForRound.map(p => {
    const perTank = machine ? calculatePerTankDosage(machine, p) : null;
    return {
      _id: p._id,
      productName: p.productName,
      amountText: perTank ? formatProductAmount(perTank) : DASH,
    };
  });

  const totalAmountColumns: DataTableColumn<ProductAmountRow>[] = [
    { key: 'productName', label: t('spray.planningSummary.product') },
    { key: 'amountText', label: t('spray.planningSummary.amount') },
  ];

  const perTankColumns: DataTableColumn<ProductAmountRow>[] = [
    { key: 'productName', label: t('spray.planningSummary.product') },
    { key: 'amountText', label: t('spray.planningSummary.perTank') },
  ];

  // Number of Tanks display + optional "exact" annotation.
  const renderTanksValue = (): React.ReactNode => {
    const tanks = calculateNumberOfTanks(totalWater?.value ?? null, machine);
    if (!tanks) {
      return (
        <>
          {DASH}
          {machineHasNoTankVolume && (
            <div className="text-muted small mt-1" data-testid="no-tank-volume-hint">
              {t('spray.planningSummary.noTankVolume')}
            </div>
          )}
        </>
      );
    }
    // Show "(N exact)" only when the unrounded value differs from
    // the ceiled count — exact divides need no annotation.
    const showExact = tanks.exactValue != null && tanks.exactValue !== tanks.value;
    return (
      <>
        {formatTankCount(tanks.value, t)}
        {showExact && (
          <span className="text-muted small ms-2">
            {t('spray.planningSummary.exactTanksAnnotation', {
              value: Number.isInteger(tanks.exactValue)
                ? tanks.exactValue
                : tanks.exactValue!.toFixed(1),
            })}
          </span>
        )}
      </>
    );
  };

  return (
    <div data-testid="planning-summary">
      <DefinitionList columns={2}>
        <DataPair
          label={t('spray.planningSummary.totalPlannedWater')}
          value={totalWater ? formatLitres(totalWater.value) : DASH}
        />
      </DefinitionList>

      <h4 className="pat-form-section__title">{t('spray.planningSummary.totalProductAmounts')}</h4>
      <DataTable
        columns={totalAmountColumns}
        rows={totalAmountRows}
        rowKey={(r) => r._id}
      />

      {machine && (
        <>
          <DefinitionList columns={2}>
            <DataPair
              label={t('spray.planningSummary.numberOfTanks')}
              value={renderTanksValue()}
            />
          </DefinitionList>

          <h4 className="pat-form-section__title">{t('spray.planningSummary.perTankDosages')}</h4>
          <DataTable
            columns={perTankColumns}
            rows={perTankRows}
            rowKey={(r) => r._id}
          />
        </>
      )}
    </div>
  );
};

export default PlanningSummary;
